import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { useUnreadCount } from './UnreadCountContext'

/**
 * Refreshes the Sendbird unread message count via the REST API
 * on initial mount and every time the browser location (route) changes,
 * but not when the sendbird websocket is open
 *
 * Also resets the polling timer so the next automatic poll is a full
 * 60 seconds away (avoids a redundant poll right after a navigation
 * already fetched the count).
 *
 * Place this hook in a component that is always mounted (e.g. the
 * navigation sidebar) so that the count is kept up-to-date as the
 * clinician navigates between pages.
 */
export const useRefreshUnreadOnNavigation = () => {
  const location = useLocation()
  const {
    refreshUnreadCount,
    resetPollingTimer,
    isWebsocketActive,
  } = useUnreadCount()
  const prevPathRef = useRef<string | null>(null)

  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname

      // Skip the REST fetch when the WebSocket is connected — the
      // real-time handler is already keeping the count accurate.
      if (!isWebsocketActive) {
        refreshUnreadCount()
        // A fresh count was just requested — restart the polling timer
        // so the next automatic poll is a full interval away.
        resetPollingTimer()
      }
    }
  }, [
    location.pathname,
    refreshUnreadCount,
    resetPollingTimer,
    isWebsocketActive,
  ])
}
